import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Legend
} from 'recharts';
import {
  ArrowLeft, TrendingUp, TrendingDown, Minus, Award, Target,
  BarChart3, Users, CheckCircle, AlertCircle, Download, HelpCircle
} from 'lucide-react';
import { PartnerLogos } from '../components/PartnerLogos';

const BRAND_LOGO_LIGHT = "/benchmark-ph-light.png";

// Performance Badge Component
function PerformanceBadge({ performance }) {
  const config = {
    better: { icon: TrendingUp, color: 'text-emerald-600 bg-emerald-50', label: 'Above Average' },
    same: { icon: Minus, color: 'text-slate-600 bg-slate-100', label: 'On Par' },
    below: { icon: TrendingDown, color: 'text-amber-600 bg-amber-50', label: 'Below Average' },
    no_data: { icon: AlertCircle, color: 'text-slate-400 bg-slate-50', label: 'No Data' },
    no_comparison: { icon: Minus, color: 'text-blue-500 bg-blue-50', label: 'First Submission' }
  };
  
  const { icon: Icon, color, label } = config[performance] || config.no_data;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// Score Card Component
function ScoreCard({ title, userScore, industryScore, icon: Icon, hasComparison }) {
  const showComparison = hasComparison && industryScore !== null && industryScore > 0;
  const diff = showComparison ? userScore - industryScore : 0;
  const isPositive = diff >= 0;
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-accent" />
        </div>
        <h3 className="font-semibold text-slate-700">{title}</h3>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-slate-500 mb-1">Your Score</p>
          <p className="text-3xl font-bold text-primary">{userScore}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">Industry Avg</p>
          <p className="text-3xl font-bold text-slate-400">
            {showComparison ? industryScore : 'N/A'}
          </p>
        </div>
      </div>
      {showComparison ? (
        <div className={`mt-4 flex items-center gap-2 ${isPositive ? 'text-emerald-600' : 'text-amber-600'}`}>
          {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          <span className="text-sm font-medium">
            {Math.abs(diff).toFixed(2)} {isPositive ? 'above' : 'below'} average
          </span>
        </div>
      ) : (
        <div className="mt-4 flex items-center gap-2 text-blue-500">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm font-medium">First submission - comparison coming soon</span>
        </div>
      )}
    </div>
  );
}

export default function ComparisonPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await api.get('/api/compliance/report', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'benchmark_ph_member_report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded — includes your signed compliance acceptance');
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    loadComparison();
  }, []);

  const loadComparison = async () => {
    try {
      const res = await api.get('/api/questionnaire/comparison');
      setData(res.data);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Please complete the questionnaire first');
        navigate('/questionnaire');
      } else {
        toast.error('Failed to load comparison data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Prepare radar chart data
  const radarData = data?.materiality_comparison?.map(item => ({
    subject: item.name.split(' ').slice(0, 2).join(' '),
    user: (item.user_financial + item.user_impact) / 2,
    industry: (item.industry_financial + item.industry_impact) / 2,
    fullName: item.name
  })) || [];

  // Prepare bar chart data for KPIs
  const kpiChartData = data?.kpi_comparison?.filter(k => k.user_value > 0 || k.industry_average > 0).map(item => ({
    name: item.label.replace(' (%)', '').replace(' (tCO₂e)', ''),
    Your: item.user_value,
    Industry: item.industry_average,
    performance: item.performance
  })) || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">No comparison data available</p>
          <Link to="/questionnaire" className="text-accent hover:underline mt-2 inline-block">
            Complete Questionnaire
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={BRAND_LOGO_LIGHT} alt="Benchmark PH" className="h-10 object-contain" />
            <div>
              <h1 className="font-semibold text-primary">ESG Comparison Report</h1>
              <p className="text-xs text-slate-500">{user?.company_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/guide"
              className="text-slate-500 hover:text-primary p-2"
              title="User Guide"
              data-testid="guide-link"
            >
              <HelpCircle className="w-5 h-5" />
            </Link>
            <button
              onClick={downloadReport}
              disabled={downloading}
              className="btn-accent h-10 px-4 flex items-center gap-2 text-sm disabled:opacity-50"
              data-testid="download-report-btn"
            >
              <Download className="w-4 h-4" />
              {downloading ? 'Preparing…' : 'Download Report'}
            </button>
            <Link
              to="/questionnaire"
              className="text-slate-500 hover:text-primary flex items-center gap-2 text-sm"
              data-testid="back-to-questionnaire"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary via-slate-800 to-primary rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <Award className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Your ESG Performance Report</h2>
              <p className="text-slate-300">
                {data.has_comparison_data 
                  ? `Compared against ${data.total_respondents} industry respondents`
                  : 'Your submission has been recorded successfully'}
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ScoreCard 
              title="Overall ESG Score" 
              userScore={data.overall_score.user}
              industryScore={data.overall_score.industry}
              icon={Target}
              hasComparison={data.has_comparison_data}
            />
            <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
              <p className="text-slate-300 text-sm mb-2">Your Business Segment</p>
              <p className="text-2xl font-bold">{data.user_company || 'N/A'}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
              <p className="text-slate-300 text-sm mb-2">Industry Benchmark</p>
              <p className="text-2xl font-bold">
                {data.has_comparison_data ? `${data.total_respondents} Companies` : 'Coming Soon'}
              </p>
              <p className="text-slate-400 text-sm mt-1">
                {data.has_comparison_data 
                  ? 'Contributing to benchmark' 
                  : 'You are among the first respondents!'}
              </p>
            </div>
          </div>

          {!data.has_comparison_data && (
            <div className="mt-6 p-4 bg-blue-500/20 rounded-xl border border-blue-400/30">
              <p className="text-blue-100 text-sm">
                <strong>Note:</strong> Industry comparison data will be available once more respondents complete the questionnaire. 
                Your data has been recorded and will be included in the benchmark calculations.
              </p>
            </div>
          )}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Radar Chart - Materiality */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="materiality-radar">
            <h3 className="text-lg font-semibold text-primary mb-4">Materiality Assessment</h3>
            <p className="text-sm text-slate-500 mb-4">
              {data.has_comparison_data 
                ? 'Your materiality ratings vs industry average'
                : 'Your materiality ratings'}
            </p>
            <ResponsiveContainer width="100%" height={350}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E2E8F0" />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#64748B' }} />
                <PolarRadiusAxis angle={30} domain={[0, 5]} tick={{ fontSize: 10 }} />
                <Radar
                  name="Your Company"
                  dataKey="user"
                  stroke="#2563EB"
                  fill="#2563EB"
                  fillOpacity={0.3}
                />
                {data.has_comparison_data && (
                  <Radar
                    name="Industry Average"
                    dataKey="industry"
                    stroke="#10B981"
                    fill="#10B981"
                    fillOpacity={0.2}
                  />
                )}
                <Legend />
                <RechartsTooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Bar Chart - KPIs */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="kpi-comparison">
            <h3 className="text-lg font-semibold text-primary mb-4">KPI Performance Comparison</h3>
            <p className="text-sm text-slate-500 mb-4">Key performance indicators vs industry</p>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={kpiChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="Your" fill="#2563EB" radius={[0, 4, 4, 0]} />
                <Bar dataKey="Industry" fill="#94A3B8" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Detailed KPI Table */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mb-8" data-testid="kpi-table">
          <h3 className="text-lg font-semibold text-primary mb-4">Detailed KPI Breakdown</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">KPI</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Your Value</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Industry Avg</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.kpi_comparison.map((kpi, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm font-medium text-slate-700">{kpi.label}</td>
                    <td className="py-3 px-4 text-sm text-right font-mono">{kpi.user_value || '-'}</td>
                    <td className="py-3 px-4 text-sm text-right font-mono text-slate-500">{kpi.industry_average || '-'}</td>
                    <td className="py-3 px-4 text-center">
                      <PerformanceBadge performance={kpi.performance} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Materiality Details */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="materiality-table">
          <h3 className="text-lg font-semibold text-primary mb-4">Materiality Assessment Details</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">ESG Issue</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600" colSpan="2">Financial Relevance</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600" colSpan="2">Impact Relevance</th>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th></th>
                  <th className="text-center py-2 px-2 text-xs text-slate-500">You</th>
                  <th className="text-center py-2 px-2 text-xs text-slate-500">Industry</th>
                  <th className="text-center py-2 px-2 text-xs text-slate-500">You</th>
                  <th className="text-center py-2 px-2 text-xs text-slate-500">Industry</th>
                </tr>
              </thead>
              <tbody>
                {data.materiality_comparison.map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm font-medium text-slate-700">{item.name}</td>
                    <td className="py-3 px-2 text-center">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg font-bold text-sm ${
                        item.user_financial >= item.industry_financial ? 'bg-accent/10 text-accent' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {item.user_financial}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center text-sm text-slate-500">{item.industry_financial}</td>
                    <td className="py-3 px-2 text-center">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg font-bold text-sm ${
                        item.user_impact >= item.industry_impact ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {item.user_impact}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center text-sm text-slate-500">{item.industry_impact}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-4 py-6 text-center text-slate-500 text-sm border-t border-slate-200 bg-white mt-8">
        <PartnerLogos className="mb-3" />
        Developed for <span className="text-slate-600">IBPAP</span> by{' '}
        <a 
          href="https://sustek.ai/solutions/soctech" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-emerald-600 hover:text-emerald-700 hover:underline"
        >
          SusTek SocTech Labs
        </a>
      </footer>
    </div>
  );
}
